import { loadEnv } from "@build/index";
import Axios from "axios";
import { stringify } from "qs";

import { i18nHttpMaps } from "@/plugins/i18n";
import { useChannelStoreHook } from "@/store/modules/channel";
import { useHttpStoreHook } from "@/store/modules/http";
import { useUserStoreHook } from "@/store/modules/user";
import { formatToken } from "@/utils/auth";

import { getInstanceId } from "../index";

const { VITE_PROXY_DOMAIN } = loadEnv();

const DEFAULT_LANGUAGE = "zh";

const _language = localStorage.getItem("language") || DEFAULT_LANGUAGE;

// 相关配置请参考：www.axios-js.com/zh-cn/docs/#axios-request-config-1
const defaultConfig = {
  baseURL: VITE_PROXY_DOMAIN,
  // 请求超时时间
  timeout: 60000,
  headers: {
    Accept: "application/json, text/plain, */*",
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    "y-raw-required": "false",
    "y-src": "ysys-web",
    "y-client": "y-platform",
    "y-platform-language": _language,
    "y-client-instance-id": getInstanceId()
  },
  // 数组格式参数序列化（https://github.com/axios/axios/issues/5142）
  paramsSerializer: {
    serialize: stringify
  },
  withCredentials: true
};

const setHeader = config => {
  const channelKey = new URLSearchParams(window.location.href).get(
    "channelKey"
  );
  config.headers["y-platform-channel"] =
    channelKey || useChannelStoreHook().channel?.key || "";
  const language = useUserStoreHook().language || DEFAULT_LANGUAGE;
  config.headers["y-platform-language"] = i18nHttpMaps[language] || language;
  config.headers = Object.assign(config.headers, useHttpStoreHook().headers);
  const container = new URLSearchParams(window.location.href).get("container");
  if (container === "vapp") {
    config.headers["y-embeded"] = true;
  }
  return config;
};

class PureHttp {
  constructor() {
    this.#httpInterceptorsRequest();
    this.#httpInterceptorsResponse();
  }

  /** token过期后，暂存待执行的请求 */
  #requests = [];

  /** 防止重复刷新token */
  #isRefreshing = false;

  /** 初始化配置对象 */
  #initConfig = {};

  /** 保存当前Axios实例对象 */
  #axiosInstance = Axios.create(defaultConfig);

  /** 重连原始请求 */
  #retryOriginalRequest(config) {
    return new Promise(resolve => {
      this.#requests.push(token => {
        config.headers["Authorization"] = formatToken(token);
        resolve(config);
      });
    });
  }

  /** 请求拦截 */
  #httpInterceptorsRequest() {
    this.#axiosInstance.interceptors.request.use(
      async config => {
        config = setHeader(config);
        // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
        if (typeof config.beforeRequestCallback === "function") {
          config.beforeRequestCallback(config);
          return config;
        }
        if (this.#initConfig.beforeRequestCallback) {
          this.#initConfig.beforeRequestCallback(config);
          return config;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );
  }

  /** 响应拦截 */
  #httpInterceptorsResponse() {
    const instance = this.#axiosInstance;
    instance.interceptors.response.use(
      response => {
        const $config = response.config;
        // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
        if (typeof $config.beforeResponseCallback === "function") {
          $config.beforeResponseCallback(response);
          return response.data;
        }
        if (this.#initConfig.beforeResponseCallback) {
          this.#initConfig.beforeResponseCallback(response);
          return response.data;
        }
        return response.data;
      },
      error => {
        const $error = error;
        $error.isCancelRequest = Axios.isCancel($error);
        // 所有的响应异常 区分来源为取消请求/非取消请求
        return Promise.reject($error);
      }
    );
  }

  /** 通用请求工具函数 */
  request(method, url, param = {}, axiosConfig = {}) {
    const config = {
      method,
      url,
      ...param,
      ...axiosConfig
    };

    // 单独处理自定义请求/响应回调
    return new Promise((resolve, reject) => {
      this.#axiosInstance
        .request(config)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /** 单独抽离的post工具函数 */
  post(url, params, config) {
    return this.request("post", url, params, config);
  }

  /** 单独抽离的get工具函数 */
  get(url, params, config) {
    return this.request(
      "get",
      url,
      {},
      {
        ...config,
        params
      }
    );
  }

  patch(url, params, config) {
    return this.request("patch", url, params, config);
  }
}

export const http = new PureHttp();
